import { useCallback, useState, useContext, useEffect } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { Redirect, useParams, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { t } from 'ttag'

import { Globals } from '@h2/globals'
import { usePageTitle } from '@h2/common/components/hooks/usePageTitle'
import navianceH2WhiteSvg from '@h2/assets/svg/illustrations/navianceH2White.svg'
import times from '@h2/assets/svg/illustrations/times.svg'
import { InfoCircleIcon } from '@h2/components/icons/infoCircle'
import { features } from '@h2/components/media'
import { NAVIANCEBLUE, PG } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { vagRundFont } from '@h2/components/mixins'
import { Box } from '@h2/components/layout/box'
import { Link } from '@h2/common/components/link'
import { Card } from '@h2/common/components/card'
import { Option, OptionGroup } from '@h2/components/containers/optionGroup'
import { CheckIcon, TimesIcon } from '@h2/components/icons'
import { Flex } from '@h2/components/layout/flex'
import { Modal } from '@h2/components/containers/modal'
import { Alert } from '@h2/components/display/alert'
import { Spinner } from '@h2/components/loading/spinner'
import { api, errors } from '@h2/api'

import { OptionBody } from './components'
import { props } from './props'

import { Create } from '../highlights/create'

const Image = styled.img`
  width: 214px;
  height: 68px;
  opacity: 1;
`
const Wrap = styled(Flex)`
  height: 100%;
  flex-direction: column;

  > ul {
    flex-direction: column;
  }

  @media (${features.minWidth.laptop}) {
    > ul {
      flex-direction: row;

      > li {
        min-height: 215px;
      }
    }
  }
`
const StyledLink = styled(Link)`
  text-decoration: underline;
  cursor: pointer;
  color: ${PG};
`
const StyledHeading = styled(Heading)`
  line-height: normal;
  @media (${features.minWidth.laptop}) {
    align-self: center;
    p {
      width: 480px;
    }
  }
`
const InfoHeading = styled(Heading)`
  display: flex;
  align-items: center;
`
const Icon = styled.span`
  margin-right: 23px;

  > span {
    vertical-align: middle;
  }
`
// Navaince Link Cancel CSS
const ImageTimes = styled.img`
  width: 120px;
  height: auto;

  @media (${features.minWidth.laptop}) {
    width: 120px;
  }
`
const HeadingLinkCancel = styled(Heading)`
  display: flex;
  flex-direction: column;
  margin: 0;
  font-size: 24px;
  line-height: 28px;

  > span[role='img'] {
    margin-bottom: 15px;

    svg {
      width: auto;
      height: 130px;
    }
  }

  @media (${features.minWidth.laptop}) {
    font-size: 30px;
    line-height: 34px;
  }
`
const WrapLinkCancel = styled(Flex)`
  height: 100%;
  flex-direction: column;
`
const ReturnToNavianceLink = styled(Link)`
  ${vagRundFont};
  padding: 10px 18px;
  color: white;
  width: 100%;

  @media (${features.minWidth.laptop}) {
    width: 250px;
    height: auto;
  }
`
const RestartLink = styled(Link)`
  ${vagRundFont};
  padding: 10px 18px;
  color: ${PG};
  background-color: white;
  border: 2px solid ${PG};
  width: 100%;
  margin-bottom: 10px;
  padding-left: 5px;

  @media (${features.minWidth.laptop}) {
    width: 220px;
    height: auto;
    margin-bottom: 0;
  }
`
const LinkBox = styled(Box)`
  display: block;
  margin-top: 13px;

  @media (${features.minWidth.laptop}) {
    width: 100%;
    display: flex;
    justify-content: space-evenly;
  }
`
const ImageBox = styled(Box)`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`
const SpaceBox = styled(Box)`
  display: flex;
  justify-content: space-between;

  @media (${features.minWidth.tablet}) {
    margin: 4px;
  }
`
const NavianceLinkAccount = ({ inDialog }) => {
  const { backToNavianceURL } = useContext(Globals)
  const [isOpen, setIsOpen] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [redirect, setRedirect] = useState(null)
  const queryClient = useQueryClient()
  const params = useParams()
  const browserHistory = useHistory()

  useEffect(() => {
    const handlePopState = () => {
      // "disable" the browser's back button for this page
      browserHistory.push('/naviancelinkaccount/register')
    }

    window.onpopstate = handlePopState

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [browserHistory])
  const NavianceIntegrationModal = useCallback(() => {
    return (
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <Modal.Header>
          <InfoHeading level="2">
            <Icon>
              <InfoCircleIcon size="large" />
            </Icon>
            {t`Naviance Integration`}
          </InfoHeading>
        </Modal.Header>
        <Modal.Body>
          <Text weight="bold" as="span" color="#0F1820">{t`What is Headed2?`}</Text>
          <Text color="#0F1820">
            {t`Headed2 is a leading provider of state-specific career exploration tools for schools, districts, counties, and entire states. For over 15 years, Headed2 has been helping students plan for their future and explore the world of work. In that time, Headed2 has grown into a trusted platform that serves millions of individuals each year from across the United States.`}
          </Text>
          <Text weight="bold" as="span" color="#0F1820">{t`Why should I link?`}</Text>
          <Text color="#0F1820">
            {t`Link your Naviance account to Headed2 and start using Headed2’s career tools to plan and prepare for your future, connect with job opportunities, and more. Naviance does not share student data with Headed2.`}
          </Text>
          <Text weight="bold" as="span" color="#0F1820">
            Can I change my mind?
          </Text>
          <Text color="#0F1820">
            {t`Yes, you can unlink your Headed2 and Naviance accounts. Go to My Account in the About Me section of Naviance Student or your Profile page in Headed2. You will not be able to access Headed2 features in Naviance but your Headed2 information will still be available if you re-link or sign in to Headed2 directly.`}
          </Text>
        </Modal.Body>
      </Modal>
    )
  }, [isOpen, setIsOpen])
  const onClickReturnNavaince = useCallback(
    evt => {
      evt.preventDefault()
      window.location.replace(backToNavianceURL)
    },
    [backToNavianceURL]
  )
  const NavianceLinkCancel = useCallback(() => {
    return (
      <Card useDetour>
        <Card.Highlight color={NAVIANCEBLUE}>
          <Create isNamePanel type="navianceUser" inRegistration={false} />
        </Card.Highlight>
        <Card.Content>
          <WrapLinkCancel>
            <ImageBox>
              <ImageTimes src={times} />
            </ImageBox>
            <HeadingLinkCancel level="2" align="center">
              <span>{t`Linking cancelled`}</span>
            </HeadingLinkCancel>
            <Text align="center" size="large" my="20px">
              {t`Your Headed2 account has not been linked to your Naviance account.`}
            </Text>
            <LinkBox width="100%" alignSelf="center">
              <RestartLink
                variant="button"
                fontSize="20px"
                weight="normal"
                onClick={() => setIsCancelled(false)}>
                {t`Restart Linking`}
              </RestartLink>
              <SpaceBox></SpaceBox>
              <ReturnToNavianceLink
                width="auto"
                variant="button"
                fontSize="20px"
                weight="normal"
                onClick={onClickReturnNavaince}>
                {t`Return to Naviance`}
              </ReturnToNavianceLink>
            </LinkBox>
          </WrapLinkCancel>
        </Card.Content>
      </Card>
    )
  }, [setIsCancelled, onClickReturnNavaince])
  const { mutate: onApproveLinking, isLoading: isApproved } = useMutation(
    () =>
      api.fetch(`/external/authorizations/complete`, {
        method: 'POST'
      }),
    {
      onSuccess: () => {
        let pathname = 'register'

        if (params.type === 'signin') {
          pathname = 'signin'
        }

        queryClient.refetchQueries('islinkedAccount', { refetchType: 'all' })
        setRedirect({
          pathname: '/accept',
          state: { panel: pathname }
        })
      },
      onError: err => {
        setErrorMsg(errors.getMessage(err))
      }
    }
  )

  usePageTitle(t`Naviance Link Account`, inDialog)

  if (redirect) {
    return <Redirect push to={{ ...redirect }} />
  }

  if (isCancelled) {
    return <NavianceLinkCancel />
  }

  return (
    <>
      <Card useDetour inDialog={inDialog}>
        <Card.Highlight color={NAVIANCEBLUE}>
          <Create isNamePanel type="navianceUser" inRegistration={false} />
        </Card.Highlight>
        <Card.Content>
          <Wrap>
            <StyledHeading level="2" align="center">
              <span>{t`Link account`}</span>
              <Box align="center">
                <Text align="center" size="large" margin="0" mb="30px">
                  {t`Approve or cancel linking your Naviance and Headed2 accounts. You can unlink the accounts if you change your mind.`}
                </Text>
              </Box>
              <Image src={navianceH2WhiteSvg} alt="" />
            </StyledHeading>
            <Alert
              margin="0 0 15px 0"
              type="error"
              isVisible={Boolean(errorMsg)}
              message={errorMsg}
              onClose={() => setErrorMsg('')}
            />
            <OptionGroup gap="15px" height="auto">
              <Option value="false">
                <OptionBody onClick={() => setIsCancelled(true)}>
                  <TimesIcon label="icon" size="large" />
                  <span className="flex">
                    <span level="4" removeMargins>
                      {t`Cancel`}
                    </span>
                    <span>{t`I don’t want to link the accounts.`}</span>
                  </span>
                </OptionBody>
              </Option>
              <Option value="true">
                <OptionBody onClick={onApproveLinking}>
                  {isApproved ? (
                    <Spinner size="large" color={PG} />
                  ) : (
                    <CheckIcon label="icon" size="large" />
                  )}
                  <span className="flex">
                    <span level="4" removeMargins>
                      {t`Approve`}
                    </span>
                    <span>{t`Link my accounts to start using Headed2 features.`}</span>
                  </span>
                </OptionBody>
              </Option>
            </OptionGroup>
            <StyledLink onClick={() => setIsOpen(true)}>
              <Text
                color={PG}
                align="center"
                size="medium"
                weight="bold"
                mb="16px"
                margin="20px 0 0 0">
                {t`Naviance will not share data with Headed2`}
              </Text>
            </StyledLink>
          </Wrap>
        </Card.Content>
      </Card>
      <NavianceIntegrationModal />
    </>
  )
}

NavianceLinkAccount.propTypes = { ...props }

export { NavianceLinkAccount }
